import { useEffect, useState } from 'react'

import compact from 'lodash/compact'

import policyAuthorizations from 'modules/permissions/store/collections/policyAuthorizations'
import type { PolicyTarget } from 'modules/permissions/store/collections/policyAuthorizations'

interface AsyncCanDataType {
  success: boolean
  error?: unknown
  authorizations?: typeof policyAuthorizations
}

/**
 * @deprecated Use the PolicyAuthorizations collection instead. For more info about how to use it,
 * the 'How to add new permissions' documentation at: https://www.notion.so/factorialco/How-to-add-new-permissions-504453f2b76b4bdba23feb632158ecaa
 */
async function asyncCan(
  policyKey: string,
  targets: PolicyTarget[] = []
): Promise<AsyncCanDataType> {
  let result

  try {
    result = await policyAuthorizations.authorizePolicies(policyKey, compact(targets))
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error(error, JSON.stringify(error))

    return Promise.resolve({
      success: false,
      error: error,
    })
  }

  return Promise.resolve({
    success: true,
    authorizations: result,
  })
}
interface Return {
  loading: boolean
  value: boolean
}

/**
 * @deprecated Don't get policies through `useAsyncCan`. Check them through GraphQL in your page components.
 */
export const useAsyncCan = (policyKey: string, target?: PolicyTarget): Return => {
  const [loading, setLoading] = useState(true)
  const [value, setValue] = useState(false) // Conservative value

  /* eslint-disable react-hooks/exhaustive-deps */
  useEffect(() => {
    const loadData = async () => {
      setLoading(true)
      await asyncCan(policyKey, target ? [target] : [])
      setValue(policyAuthorizations.for(policyKey, target))
      setLoading(false)
    }

    loadData()
  }, [])

  /* eslint-enable react-hooks/exhaustive-deps */
  return {
    loading,
    value,
  }
}
interface Authorization {
  for: (target: PolicyTarget) => boolean
}
interface BulkReturn {
  loading: boolean
  authorizations: Authorization
}

/**
 * @deprecated Use the PolicyAuthorizations collection instead. For more info about how to use it,
 * the 'How to add new permissions' documentation at: https://www.notion.so/factorialco/How-to-add-new-permissions-504453f2b76b4bdba23feb632158ecaa
 */
const useAsyncCanBulk = (policyKey: string, targets: PolicyTarget[]): BulkReturn => {
  const [loading, setLoading] = useState(true)

  /* eslint-disable react-hooks/exhaustive-deps */
  useEffect(() => {
    if (targets.length === 0) return

    const loadData = async () => {
      setLoading(true)
      await asyncCan(policyKey, targets)
      setLoading(false)
    }

    loadData()
  }, [targets])

  // TODO: deprecate this. It's bailing out a lot of memoizations
  /* eslint-enable react-hooks/exhaustive-deps */
  const authorizations = {
    for: (target: PolicyTarget) => {
      return policyAuthorizations.for(policyKey, target)
    },
  }

  return {
    loading,
    authorizations,
  }
}
